import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Drawer,
} from "@totalenergiescode/mobility-business-rev-design-system";
import { FC, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useCreateFormDataRuleGroup } from "@/api/graphql/formDataInjector/createFormDataRuleGroup/hooks";
import { useSearchGroups } from "@/api/graphql/ledgers/searchGroups/hooks";
import {
  LedgersMsSearchGroupsResponseData,
  MiddleMsCreateFormDataRuleGroupRequestType,
} from "@/api/graphql/types/graphql";
import { formatStringToArray } from "@/components/formDataInjector/utils";
import { ControlledSelect } from "@/components/shared/ControlledSelect";
import { ControlledTextField } from "@/components/shared/ControlledTextField";
import { ControlledCheckbox } from "@/components/shared/ControllerCheckbox";
import { YamlEditor } from "@/components/shared/YamlEditor";
import { DEFAULT_YAML } from "@/components/shared/YamlEditor/constants";
import { useAddSnackbar } from "@/contexts/snackbar";
import {
  CreationFormRuleSchema,
  CreationFormRuleType,
} from "@/helpers/formDataInjector/helpers";

import {
  StyledDrawerInnerContainer,
  StyledForm,
  StyledInputContainer,
  StyledRoundedTopLeftContainer,
  StyledSection,
  StyledText,
} from "./styles";
import { RulesDrawerProps } from "./type";

const defaultFormValues: CreationFormRuleType = {
  name: "",
  description: undefined,
  codes: "",
  enabled: false,
  enabledFor: "",
  disabledFor: "",
  groupCodeFor: [],
  rules: DEFAULT_YAML,
  type: undefined,
};

export const RulesDrawer: FC<RulesDrawerProps> = ({
  isCreateModalOpen,
  setIsCreateModalOpen,
}) => {
  const { mutateAsync, isLoading } = useCreateFormDataRuleGroup();
  const addSnackbar = useAddSnackbar();
  const { t: tFormDataInjector } = useTranslation("formDataInjector");
  const { data: groups, isLoading: isLoadingGroups } = useSearchGroups();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreationFormRuleType>({
    resolver: zodResolver(CreationFormRuleSchema),
    defaultValues: defaultFormValues,
  });

  useEffect(() => {
    if (isCreateModalOpen) {
      reset(defaultFormValues);
    }
  }, [isCreateModalOpen, reset]);

  const onSubmit = (values: CreationFormRuleType) => {
    const { groupCodeFor, ...rest } = values;
    mutateAsync({
      ...rest,
      enabledFor: rest.enabledFor
        ? formatStringToArray(rest.enabledFor)
        : undefined,
      disabledFor: rest.disabledFor
        ? formatStringToArray(rest.disabledFor)
        : undefined,
      codes: rest.codes ? formatStringToArray(rest.codes) : undefined,
      groupCodeFor:
        groupCodeFor && groupCodeFor.length > 0 ? groupCodeFor : undefined,
    })
      .then(() => {
        addSnackbar({
          title: tFormDataInjector(
            "formDataInjector.forms.create.success.title",
          ),
          message: tFormDataInjector(
            "formDataInjector.forms.create.success.message",
          ),
          type: "success",
          dataTestId: "formdata-injector__success-snackbar",
          isAutoClose: true,
        });
        reset(defaultFormValues);
        setIsCreateModalOpen(false);
      })
      .catch(() => {
        addSnackbar({
          title: tFormDataInjector("formDataInjector.forms.create.error.title"),
          message: tFormDataInjector(
            "formDataInjector.forms.create.error.message",
          ),
          type: "error",
          isAutoClose: true,
          dataTestId: "formdata-injector__error-snackbar",
        });
      });
  };

  const isButtonDisabled = Object.keys(errors).length > 0 || isLoading;

  return (
    <Drawer
      isOpen={isCreateModalOpen}
      width={"60%"}
      dataTestId='formdata-injector__create-rule-drawer'
      onClose={() => {
        reset(defaultFormValues);
        setIsCreateModalOpen(false);
      }}
    >
      <StyledText sx={{ margin: "20px 20px 0 20px" }} variant='title5'>
        {tFormDataInjector("formDataInjector.drawer.title")}
      </StyledText>

      <StyledRoundedTopLeftContainer>
        <StyledDrawerInnerContainer>
          <StyledForm onSubmit={handleSubmit(onSubmit)}>
            <StyledSection>
              <StyledInputContainer>
                <StyledText variant='title2' fontWeight='medium'>
                  {tFormDataInjector("formDataInjector.forms.name")}
                </StyledText>
                <ControlledTextField
                  name='name'
                  control={control}
                  namespace='formDataInjectorCreation'
                  textFieldProps={{
                    label: tFormDataInjector("formDataInjector.forms.name"),
                    dataTestId: "formdata-injector__form-rule-name",
                  }}
                />
              </StyledInputContainer>

              <StyledInputContainer>
                <StyledText variant='title2' fontWeight='medium'>
                  {tFormDataInjector("formDataInjector.forms.description")}
                </StyledText>
                <ControlledTextField
                  name='description'
                  control={control}
                  namespace='formDataInjectorCreation'
                  textFieldProps={{
                    label: tFormDataInjector(
                      "formDataInjector.forms.description",
                    ),
                    dataTestId: "formdata-injector__form-rule-description",
                  }}
                />
              </StyledInputContainer>
              <StyledInputContainer>
                <StyledText variant='title2' fontWeight='medium'>
                  {tFormDataInjector("formDataInjector.forms.rules")}
                </StyledText>
                <Controller
                  name='rules'
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <YamlEditor
                      value={value ?? ""}
                      onChange={onChange}
                      dataTestId='formdata-injector__form-rule-rules'
                    />
                  )}
                />
              </StyledInputContainer>

              <StyledInputContainer>
                <StyledText variant='title2' fontWeight='medium'>
                  {tFormDataInjector("formDataInjector.forms.enabled")}
                </StyledText>
                <ControlledCheckbox name='enabled' control={control} />
              </StyledInputContainer>

              <StyledInputContainer>
                <StyledText variant='title2' fontWeight='medium'>
                  {tFormDataInjector("formDataInjector.forms.type")}
                </StyledText>
                <ControlledSelect
                  name='type'
                  control={control}
                  namespace='formDataInjectorCreation'
                  selectProps={{
                    label: tFormDataInjector("formDataInjector.forms.type"),
                    dataTestId: "formdata-injector__form-rule-type",
                    type: "options",
                    options: [
                      {
                        label: tFormDataInjector(
                          "formDataInjector.forms.creation",
                        ),
                        value:
                          MiddleMsCreateFormDataRuleGroupRequestType.FormRuleTypeCreation,
                      },
                      {
                        label: tFormDataInjector(
                          "formDataInjector.forms.modification",
                        ),
                        value:
                          MiddleMsCreateFormDataRuleGroupRequestType.FormRuleTypeModification,
                      },
                      {
                        label: tFormDataInjector(
                          "formDataInjector.forms.createOrEdit",
                        ),
                        value:
                          MiddleMsCreateFormDataRuleGroupRequestType.FormRuleTypeCreationOrModification,
                      },
                      {
                        label: tFormDataInjector("formDataInjector.forms.copy"),
                        value:
                          MiddleMsCreateFormDataRuleGroupRequestType.FormRuleTypeCopy,
                      },
                    ],
                  }}
                />
              </StyledInputContainer>

              <StyledInputContainer>
                <StyledText variant='title2' fontWeight='medium'>
                  {tFormDataInjector("formDataInjector.forms.ledgerGroup")}
                </StyledText>
                <ControlledSelect
                  name='groupCodeFor'
                  control={control}
                  namespace='formDataInjectorCreation'
                  selectProps={{
                    label: tFormDataInjector(
                      "formDataInjector.forms.ledgerGroup",
                    ),
                    dataTestId: "formdata-injector__form-rule-ledger-group",
                    type: "options",
                    multiple: true,
                    disabled: isLoadingGroups,
                    options: groups.map(
                      (group: LedgersMsSearchGroupsResponseData) => ({
                        label: group.codeHint,
                        value: group.code,
                      }),
                    ),
                  }}
                />
              </StyledInputContainer>

              <StyledInputContainer>
                <StyledText variant='title2' fontWeight='medium'>
                  {tFormDataInjector("formDataInjector.forms.enabledFor")}
                </StyledText>
                <ControlledTextField
                  name='enabledFor'
                  control={control}
                  namespace='formDataInjectorCreation'
                  textFieldProps={{
                    label: tFormDataInjector(
                      "formDataInjector.forms.enabledFor",
                    ),
                    dataTestId: "formdata-injector__form-rule-enabledfor",
                  }}
                />
                <StyledText
                  variant='sub2'
                  fontWeight='light'
                  fontStyle='italic'
                >
                  {tFormDataInjector(
                    "formDataInjector.forms.enabledFor.helper",
                  )}
                </StyledText>
              </StyledInputContainer>

              <StyledInputContainer>
                <StyledText variant='title2' fontWeight='medium'>
                  {tFormDataInjector("formDataInjector.forms.disabledFor")}
                </StyledText>
                <ControlledTextField
                  name='disabledFor'
                  control={control}
                  namespace='formDataInjectorCreation'
                  textFieldProps={{
                    label: tFormDataInjector(
                      "formDataInjector.forms.disabledFor",
                    ),
                    dataTestId: "formdata-injector__form-rule-disabledfor",
                  }}
                />
                <StyledText
                  variant='sub2'
                  fontWeight='light'
                  fontStyle='italic'
                >
                  {tFormDataInjector(
                    "formDataInjector.forms.disabledFor.helper",
                  )}
                </StyledText>
              </StyledInputContainer>

              <StyledInputContainer>
                <StyledText variant='title2' fontWeight='medium'>
                  {tFormDataInjector("formDataInjector.forms.codes")}
                </StyledText>
                <ControlledTextField
                  name='codes'
                  control={control}
                  namespace='formDataInjectorCreation'
                  textFieldProps={{
                    label: tFormDataInjector("formDataInjector.forms.codes"),
                    dataTestId: "formdata-injector__form-rule-codes",
                  }}
                />
                <StyledText
                  variant='sub2'
                  fontWeight='light'
                  fontStyle='italic'
                >
                  {tFormDataInjector("formDataInjector.forms.codes.helper")}
                </StyledText>
              </StyledInputContainer>

              <Button
                type='submit'
                dataTestId='formdata-injector__create-rule-drawer-create-button'
                onClick={undefined}
                variant='primary'
                disabled={isButtonDisabled}
                buttonText={tFormDataInjector(
                  "formDataInjector.forms.create.button",
                )}
              />
            </StyledSection>
          </StyledForm>
        </StyledDrawerInnerContainer>
      </StyledRoundedTopLeftContainer>
    </Drawer>
  );
};
